import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isAboutPage, setIsAboutPage] = useState(false);

  useEffect(() => {
    // Check if we're on the About page
    const checkAboutPage = () => {
      setIsAboutPage(window.location.pathname === '/about');
    };

    checkAboutPage();
    window.addEventListener('popstate', checkAboutPage);

    return () => window.removeEventListener('popstate', checkAboutPage);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Set scrolled state for styling
      setIsScrolled(currentScrollY > 20);

      // Hide/show navigation based on scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down & past threshold
        setIsVisible(false);
      } else {
        // Scrolling up or at top
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate to home first
    if (window.location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const mainNavLinks = [
    { name: 'Home', id: 'hero' },
    { name: 'About Us', id: 'about', isLink: true, to: '/about' },
    { name: 'Upcoming Events', id: 'upcoming-events', isLink: true, to: '/upcoming-events' },
    { name: 'Contact', id: 'contact' }
  ];

  const serviceLinks = [
    { name: 'Celebrity Management', id: 'celebrity' },
    { name: 'Event Management', id: 'events' },
    { name: 'Sponsorship Management', id: 'sponsorship' },
    { name: 'Merchandising & Gifting', id: 'merchandising' },
    { name: 'Sporting Experiences', id: 'sports' }
  ];

  // On About page, always use dark text. On other pages, use white when not scrolled
  const textColorClass = (isScrolled || isAboutPage)
    ? 'text-gray-700 hover:text-blue-600'
    : 'text-white hover:text-blue-300';

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${(isScrolled || isAboutPage)
        ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/20'
        : 'bg-transparent'
      } ${isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('hero')}>
            <div className="w-44 h-16 flex items-center justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_text-revamp-site/artifacts/6wumkzlo_image.png"
                alt="Centre Stage Sports & Celebrities Logo"
                className="w-full h-auto object-contain hover:scale-110 transition-transform duration-300"
              />
            </div>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center flex-1 max-w-3xl mx-auto">
            <div className="flex items-center justify-between w-full px-4">
              <button
                onClick={() => scrollToSection('hero')}
                className={`text-base font-medium transition-all duration-300 hover:scale-105 ${textColorClass}`}
              >
                Home
              </button>

              <Link
                to="/about"
                className={`text-base font-medium transition-all duration-300 hover:scale-105 ${textColorClass}`}
              >
                About Us
              </Link>

              {/* Services Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger className={`flex items-center gap-1 text-base font-medium transition-all duration-300 hover:scale-105 ${textColorClass} bg-transparent border-0 outline-none focus:outline-none`}>
                  Services
                  <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white/95 backdrop-blur-md border border-gray-200/20 shadow-lg rounded-lg">
                  {serviceLinks.map((service) => (
                    <DropdownMenuItem
                      key={service.id}
                      onClick={() => scrollToSection(service.id)}
                      className="cursor-pointer hover:bg-blue-50 hover:text-blue-600 transition-colors duration-300"
                    >
                      {service.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/upcoming-events"
                className={`text-base font-medium transition-all duration-300 hover:scale-105 ${textColorClass}`}
              >
                Upcoming Events
              </Link>

              <button
                onClick={() => scrollToSection('contact')}
                className={`text-base font-medium transition-all duration-300 hover:scale-105 ${textColorClass}`}
              >
                Contact
              </button>
            </div>
          </div>

          {/* Get Started Button */}
          <div className="hidden lg:block">
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl px-6 py-2"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`${(isScrolled || isAboutPage) ? 'text-gray-900' : 'text-white'}`}
            >
              {isMobileMenuOpen ? <X className="icon-navigation w-6 h-6" /> : <Menu className="icon-navigation w-6 h-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200/20">
          <div className="px-4 py-6 space-y-4">
            {mainNavLinks.map((link) => (
              link.isLink ? (
                <Link
                  key={link.id}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-300"
                >
                  {link.name}
                </Link>
              ) : (
                <button
                  key={link.id}
                  onClick={() => {
                    scrollToSection(link.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-300"
                >
                  {link.name}
                </button>
              )
            ))}

            {/* Mobile Services Section */}
            <div className="border-t border-gray-200 pt-4">
              <div className="px-4 py-2 text-sm font-semibold text-gray-500">Services</div>
              {serviceLinks.map((service) => (
                <button
                  key={service.id}
                  onClick={() => {
                    scrollToSection(service.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-300 text-sm"
                >
                  {service.name}
                </button>
              ))}
            </div>

            <Button
              onClick={() => {
                scrollToSection('contact');
                setIsMobileMenuOpen(false);
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 mt-4"
            >
              Get Started
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};